import { FC, useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import { config } from 'config/config';
import { ICasinoSchema } from 'appRedux/models/casinoModels';
import { ILatLng, distanceInMiles, formatMiles, getCasinoLatLng } from 'common/geo';

mapboxgl.accessToken = config.mapbox.key;

const WORLD_STYLE_URL = 'mapbox://styles/mapbox/dark-v11';
const FIT_PADDING = 48;
const FIT_MAX_ZOOM = 12;

interface NearbyCasinosMapProps<T extends ICasinoSchema> {
  casinos: T[];
  userLocation: ILatLng;
  nearestCasinoId?: number;
  onSelectCasino: (casino: T) => void;
}

// Popup content is built as DOM nodes (not HTML strings) so casino names from
// the database are rendered as text.
const buildPopupContent = (name: string, distance: string, onSelect: () => void): HTMLElement => {
  const root = document.createElement('div');
  root.className = 'nearby-casinos-map__popup';

  const title = document.createElement('div');
  title.className = 'nearby-casinos-map__popup-name';
  title.textContent = name;

  const meta = document.createElement('div');
  meta.className = 'nearby-casinos-map__popup-distance';
  meta.textContent = `${distance} away`;

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'nearby-casinos-map__popup-select';
  button.textContent = 'Select';
  button.addEventListener('click', onSelect);

  root.append(title, meta, button);
  return root;
};

export const NearbyCasinosMap = <T extends ICasinoSchema>({
  casinos,
  userLocation,
  nearestCasinoId,
  onSelectCasino,
}: NearbyCasinosMapProps<T>): ReturnType<FC> => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // Markers bind click handlers once per render of the marker set; read the
  // latest callback through a ref so it never goes stale.
  const onSelectRef = useRef(onSelectCasino);
  onSelectRef.current = onSelectCasino;

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: WORLD_STYLE_URL,
      center: [userLocation.lng, userLocation.lat],
      zoom: 9,
      attributionControl: false,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
    });
    map.addControl(new mapboxgl.AttributionControl({ compact: true }));
    map.touchZoomRotate.disableRotation();
    // Ionic page transitions can mount the map before its container has its
    // final width; resize once the style loads so tiles fill the box.
    map.once('load', () => map.resize());
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // The map instance lives for the component's lifetime; markers/bounds are
    // updated by the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return;
    }

    const markers: mapboxgl.Marker[] = [];

    const userEl = document.createElement('div');
    userEl.className = 'nearby-casinos-map__user';
    markers.push(
      new mapboxgl.Marker({ element: userEl }).setLngLat([userLocation.lng, userLocation.lat]).addTo(map)
    );

    let nearestCoords: ILatLng | null = null;

    casinos.forEach((casino) => {
      const coords = getCasinoLatLng(casino);
      if (!coords) {
        return;
      }
      const isNearest = casino.casinoId === nearestCasinoId;
      if (isNearest) {
        nearestCoords = coords;
      }

      const pinEl = document.createElement('div');
      pinEl.className = `nearby-casinos-map__pin${isNearest ? ' nearby-casinos-map__pin--nearest' : ''}`;
      pinEl.setAttribute('aria-label', casino.displayName);
      // Mapbox owns the marker element's `transform` (it positions it), so the
      // rotated pin shape lives on a child.
      const pinHead = document.createElement('span');
      pinHead.className = 'nearby-casinos-map__pin-head';
      pinEl.appendChild(pinHead);

      const popup = new mapboxgl.Popup({ offset: 18, closeButton: false, maxWidth: '220px' }).setDOMContent(
        buildPopupContent(
          casino.displayName,
          formatMiles(distanceInMiles(userLocation, coords)),
          () => onSelectRef.current(casino)
        )
      );

      markers.push(
        new mapboxgl.Marker({ element: pinEl, anchor: 'bottom' })
          .setLngLat([coords.lng, coords.lat])
          .setPopup(popup)
          .addTo(map)
      );
    });

    // Frame the user and their nearest casino; casinos can be states apart, so
    // fitting every pin would zoom out to a whole-country view.
    const bounds = new mapboxgl.LngLatBounds([userLocation.lng, userLocation.lat], [userLocation.lng, userLocation.lat]);
    if (nearestCoords) {
      const { lng, lat } = nearestCoords as ILatLng;
      bounds.extend([lng, lat]);
    }
    map.fitBounds(bounds, { padding: FIT_PADDING, maxZoom: FIT_MAX_ZOOM, duration: 0 });

    return () => {
      markers.forEach((marker) => marker.remove());
    };
  }, [casinos, userLocation, nearestCasinoId]);

  return <div ref={containerRef} className="nearby-casinos-map" data-testid="nearby-casinos-map" />;
};

export default NearbyCasinosMap;
